import React from 'react';
import { Check, Plus, Search, X } from 'lucide-react';
import { DMA_REGIONS, type DMARegion } from '../../../data/dmaList';
import InteractiveDmasMap from './InteractiveDmasMap';

interface DMASearchSelectorProps {
  selectedDMAs: string[];
  onChange: (next: string[]) => void;
  accentColor: string;
  // DMAs the publisher identified at onboarding (their station's coverage).
  // Surfaced as one-click "Suggested" chips so a local-station advertiser
  // doesn't have to scroll the full Nielsen list. Optional — when omitted
  // or empty, the suggested-row simply hides.
  suggestedDMACodes?: string[];
  // CTV-674: codes the buyer may pick (coverage + markets within 50 miles,
  // computed server-side). Gates the search dropdown and renders everything
  // else on the map dimmed + inert. Null/undefined = no restriction.
  allowedDMACodes?: string[] | null;
  // Show the deck.gl InteractiveDmasMap below the search (CTV-674). Defaults
  // to true (wizard usage); tests exercising just the search pass false.
  showMap?: boolean;
}

// Search-and-pick over the full DMA list. Mirrors the mockup-proposals
// DMASearchSelector but reworked for the selfservice wizard's slate-on-white
// palette and tightened to drop the proposal-flow-specific "select all"
// affordance (it doesn't make sense for an advertiser picking target DMAs).
const DMASearchSelector: React.FC<DMASearchSelectorProps> = ({
  selectedDMAs,
  onChange,
  accentColor,
  suggestedDMACodes,
  allowedDMACodes,
  showMap = true,
}) => {
  const [search, setSearch] = React.useState('');
  const [dropdownOpen, setDropdownOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (!dropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dropdownOpen]);

  // null here ≡ "no restriction" (full Nielsen list). When the caller
  // passes a list we honor it strictly, mirroring the states picker.
  const allowedSet = React.useMemo<Set<string> | null>(
    () => (allowedDMACodes && allowedDMACodes.length > 0 ? new Set(allowedDMACodes) : null),
    [allowedDMACodes],
  );

  const filtered = React.useMemo(() => {
    const term = search.trim().toLowerCase();
    const pool = allowedSet ? DMA_REGIONS.filter((d) => allowedSet.has(d.code)) : DMA_REGIONS;
    if (!term) return pool;
    return pool.filter(
      (d) => d.name.toLowerCase().includes(term) || d.code.toLowerCase().includes(term),
    );
  }, [search, allowedSet]);

  const selectedRegions = React.useMemo<DMARegion[]>(
    () => selectedDMAs
      .map((id) => DMA_REGIONS.find((d) => d.id === id))
      .filter((d): d is DMARegion => Boolean(d)),
    [selectedDMAs],
  );

  const add = (id: string) => {
    if (selectedDMAs.includes(id)) return;
    onChange([...selectedDMAs, id]);
    // CTV-689: stay open with the search cleared and focus back in the
    // input, so several metros can be picked in a row without re-opening.
    setSearch('');
    searchInputRef.current?.focus();
  };

  const remove = (id: string) => {
    onChange(selectedDMAs.filter((d) => d !== id));
  };

  const suggestions = React.useMemo<DMARegion[]>(() => {
    if (!suggestedDMACodes || suggestedDMACodes.length === 0) return [];
    return suggestedDMACodes
      .map((code) => DMA_REGIONS.find((d) => d.code === code))
      .filter((d): d is DMARegion => Boolean(d) && !selectedDMAs.includes((d as DMARegion).id));
  }, [suggestedDMACodes, selectedDMAs]);

  return (
    <div className="space-y-3">
      <div className="relative" ref={dropdownRef}>
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          ref={searchInputRef}
          type="text"
          aria-label="Search metros"
          placeholder="Search metros by name or code…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setDropdownOpen(true);
          }}
          onFocus={() => setDropdownOpen(true)}
          className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-1"
          style={{ ['--tw-ring-color' as string]: accentColor }}
        />
        {dropdownOpen && (
          <div
            role="listbox"
            aria-label="Metro search results"
            className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
          >
            {filtered.length > 0 ? (
              filtered.map((dma) => {
                const isSelected = selectedDMAs.includes(dma.id);
                // CTV-689: selected options stay clickable as a deselect
                // toggle, marked with a check so they read as selected, not
                // disabled.
                return (
                  <button
                    key={dma.id}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => (isSelected ? remove(dma.id) : add(dma.id))}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-slate-50 text-slate-900 flex items-center justify-between gap-2 ${
                      isSelected ? 'bg-slate-50' : ''
                    }`}
                  >
                    <span>
                      <span className="font-medium">{dma.code}</span>
                      <span className="text-slate-500"> — {dma.name}</span>
                    </span>
                    {isSelected && <Check className="w-3.5 h-3.5 flex-shrink-0" style={{ color: accentColor }} />}
                  </button>
                );
              })
            ) : (
              <div className="px-3 py-2 text-sm text-slate-500">No metros found</div>
            )}
          </div>
        )}
      </div>

      {showMap && (
        <>
          <InteractiveDmasMap
            selectedDMAs={selectedDMAs}
            onChange={onChange}
            accentColor={accentColor}
            focusDmaCodes={suggestedDMACodes}
            allowedDmaCodes={allowedDMACodes}
          />
          {allowedSet && (
            <p className="text-[11px] text-slate-500">
              Targeting limited to markets near this channel's coverage area.
            </p>
          )}
        </>
      )}

      {suggestions.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-2">
            Suggested metros
          </div>
          <div className="flex flex-wrap gap-1.5">
            {suggestions.map((dma) => (
              <button
                key={dma.id}
                type="button"
                // Direct onChange (not add): the chip lives outside the search
                // flow, so it shouldn't steal focus into the search input.
                onClick={() => onChange([...selectedDMAs, dma.id])}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-slate-50 text-slate-700 hover:bg-slate-100 rounded-full border border-slate-200"
              >
                <Plus className="w-3 h-3" />
                {dma.code} — {dma.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {selectedRegions.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] uppercase tracking-wide text-slate-500">
              {selectedRegions.length} selected
            </span>
            <button
              type="button"
              onClick={() => onChange([])}
              className="text-[12px] text-slate-500 hover:text-red-600 underline"
            >
              Clear all
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {selectedRegions.map((dma) => (
              <span
                key={dma.id}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-slate-100 text-slate-800 rounded-full"
              >
                {dma.code} — {dma.name}
                <button
                  type="button"
                  onClick={() => remove(dma.id)}
                  aria-label={`Remove ${dma.code}`}
                  className="text-slate-500 hover:text-red-600"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DMASearchSelector;
